/**
 * The sites the skin trusts as the page that embeds the sportsbook: skin config parentOrigins,
 * edited in rocky-admin and saved by rocky-api as exact origins (https://host[:port]), read from
 * the platform config at boot (main.js applySkinSettings).
 *
 * With a list, a parent bridge command is accepted only when MessageEvent.origin is in it, and an
 * event is posted only to the embedding page, and only when that page is in it. Without a list (a
 * skin that has none, or a page mounted before the platform config is read) the bridge works as
 * before: commands from window.parent, events with targetOrigin '*'.
 */

let trustedOrigins = [];
let warnedUntrustedParent = false;

function toOrigin(value) {
    if (typeof value !== 'string' || value.trim() === '') return null;
    try {
        return new URL(value.trim()).origin;
    } catch (err) {
        console.warn('[ParentBridge] not an origin, ignored:', value);
        return null;
    }
}

/**
 * @param {*} list - skin.parentOrigins from the platform config
 */
export function setTrustedParentOrigins(list) {
    const origins = Array.isArray(list) ? list.map(toOrigin).filter(Boolean) : [];
    trustedOrigins = [...new Set(origins)];
    warnedUntrustedParent = false;
}

/**
 * @returns {string[]}
 */
export function getTrustedParentOrigins() {
    return trustedOrigins.slice();
}

/**
 * @param {string} origin - MessageEvent.origin of a command
 * @returns {boolean} true when the skin has no list, or the origin is in it
 */
export function isTrustedParentOrigin(origin) {
    return trustedOrigins.length === 0 || trustedOrigins.includes(origin);
}

/**
 * The origin of the page that embeds this frame: location.ancestorOrigins where the browser has it
 * (Chrome, Safari), else the referrer. A referrer of our own origin (a navigation inside the
 * frame) says nothing about the parent.
 *
 * @param {Window} win - the sportsbook window
 * @returns {string|null} null when it cannot be told
 */
export function embeddingOrigin(win) {
    const location = win && win.location;
    const ancestors = location && location.ancestorOrigins;
    if (ancestors && ancestors.length > 0) return ancestors[0];

    const referrer = win && win.document && win.document.referrer;
    const origin = referrer ? toOrigin(referrer) : null;
    if (!origin || (location && origin === location.origin)) return null;
    return origin;
}

/**
 * Where an event goes: '*' without a list; the embedding page when it is in the list; nowhere
 * when it is not (logged once); every listed origin when the browser does not tell which page
 * embeds the frame (the browser delivers only to the one that matches).
 *
 * @param {Window} win - the sportsbook window
 * @returns {string[]} the targetOrigin of each postMessage
 */
export function parentTargetOrigins(win) {
    if (trustedOrigins.length === 0) return ['*'];

    const actual = embeddingOrigin(win);
    if (!actual) return trustedOrigins.slice();
    if (trustedOrigins.includes(actual)) return [actual];

    if (!warnedUntrustedParent) {
        warnedUntrustedParent = true;
        console.warn(`[ParentBridge] events not sent: the embedding page ${actual} is not in the skin's parentOrigins`);
    }
    return [];
}
